import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import _ from "lodash";
import MainLayout from "../components/layouts/MainLayout";
import InitialBooking from "../components/views/BookingList";
import CheckedInBooking from "../components/views/CheckedInBooking";
import ModalError from "../components/ModalError";
import Spinner from "../components/Spinner";
import { bookingListAction } from "../stores/actions";
import * as api from "../api/sanha";
import * as serverApi from "../api/server";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RootState } from "../stores/reducers";
import * as GoogleAnalytics from "../lib/google-analytics";
import * as Sentry from "@sentry/react";
import { logDataApi } from "../lib/logDataApi";

interface urlQueryStringParams {
  rsvnNo?: string | null;
  rsvnSeqNo?: string | null;
}

type Props = JSX.IntrinsicAttributes & { children?: React.ReactNode };

const InitialBookingContainer = (props: Props) => {
  const navigation = useNavigate();
  const [searchParams] = useSearchParams();
  const urlQueryStringParams: urlQueryStringParams = {
    rsvnNo: searchParams.get("rsvnNo"),
    rsvnSeqNo: "1",
  };
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenModalError, setIsOpenModalError] = useState(false);
  const [modalErrorMessage, setModalErrorMessage] = useState("");
  const [modalErrorSubMessage, setModalErrorSubMessage] = useState("");
  const { bookingItem, roomInfo } = useSelector((state: RootState) => state.bookingList);
  const dispatch = useDispatch();

  const openModalError = useCallback(() => {
    setIsOpenModalError(true);
  }, []);

  const closeModalError = useCallback(() => {
    setIsOpenModalError(false);
  }, []);

  const listBooking = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await serverApi.listBooking(urlQueryStringParams);

      if (response.code === "3025") {
        GoogleAnalytics.customEvent({
          category: "api_response",
          action: "api_response_fail_3025_list_booking",
          label: `${response.code}_${response.message}`,
        });
        frontPage();
      } else if (response.code !== "0000") {
        GoogleAnalytics.customEvent({
          category: "api_response",
          action: "api_response_fail_list_booking",
          label: `${response.code}_${response.message}`,
        });
        //센트리 로그 캡처 부분 (해당 캡처들이 모여서 로그로 기록을 쌓을 수 있음.)
        Sentry.captureMessage(`listBooking_Error : ${response.code}: ${response.message} (예약번호:${bookingItem.rsvnNo})`, "error");
        setModalErrorMessage("예약 조회에 실패 하였습니다.");
        throw new Error(`${response.code}, ${response.message}`);
      }
      if (response.data) {
        GoogleAnalytics.customEvent({
          category: "api_response",
          action: "api_response_ok_list_booking",
          label: `${response.data.rsvnNo}_${response.data.rsvnStatusCode}`,
        });
        dispatch(bookingListAction.setBookingItem(response.data));
        //if(!['5','6'].includes(response.data.caclPayCode)) frontPage();
        if (response.data.rsvnStatusCode !== "RR") dispatch(bookingListAction.setRoomInfo(response.pms_booking));
      } else {
        dispatch(bookingListAction.setBookingItem({}));
        dispatch(bookingListAction.setRoomInfo({}));
      }
    } catch (error: any) {
      dispatch(bookingListAction.setBookingItem({}));
      dispatch(bookingListAction.setRoomInfo({}));
      setModalErrorMessage(error.response?.data?.message);
      setModalErrorSubMessage(error.message);
      Sentry.captureMessage(`listBooking_Error : ${error.response?.data?.message} || ${error.message} (예약번호:${bookingItem.rsvnNo})`);
      openModalError();
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, urlQueryStringParams]);

  const checkOut = async () => {
    try {
      setIsLoading(true);
      const checkOutAmountResponse = await api.confirmCheckOutAmount({
        folioNo: bookingItem.folioNo || roomInfo.folioNo,
      });
      if (checkOutAmountResponse.code !== "0000" && checkOutAmountResponse.code !== "3024") {
        GoogleAnalytics.customEvent({
          category: "api_response",
          action: "api_response_fail_confirm_checkout_amount",
          label: `${checkOutAmountResponse.code}_${checkOutAmountResponse.message}`,
        });

        Sentry.captureMessage(
          `checkOut_Error : ${checkOutAmountResponse.code}: ${checkOutAmountResponse.message} (예약번호:${bookingItem.rsvnNo})`
        );
        setModalErrorMessage("퇴실 잔액 조회에 실패 하였습니다.");
        throw new Error(`${checkOutAmountResponse.code}, ${checkOutAmountResponse.message}`);
      }
      if (checkOutAmountResponse.data.balanceExist === "Y" && checkOutAmountResponse.data.balanceAmount > 0) {
        GoogleAnalytics.customEvent({
          category: "api_response",
          action: "api_response_fail_confirm_checkout_amount",
          label: `${checkOutAmountResponse.code}_${checkOutAmountResponse.data.balanceExist}_${checkOutAmountResponse.data.balanceAmount}`,
        });

        Sentry.captureMessage(`checkOut_Amount_Error : 프론트로 퇴실 처리 문의 바랍니다. (예약번호:${bookingItem.rsvnNo})`);
        setModalErrorMessage("결제 금액이 남아 있어, 체크아웃에 실패 하였습니다.");
        throw new Error("프론트로 퇴실 처리 문의 바랍니다.");
      } else {
        GoogleAnalytics.customEvent({
          category: "api_response",
          action: "api_response_ok_confirm_checkout_amount",
        });
        const checkOutResponse = await api.checkOut({
          folioNo: bookingItem.folioNo || roomInfo.folioNo,
          earlyCheckoutYN: "Y",
        });
        if (checkOutResponse.code !== "0000") {
          GoogleAnalytics.customEvent({
            category: "api_response",
            action: "api_response_fail_checkout_request",
            label: `${checkOutResponse.code}_${checkOutResponse.message}`,
          });
          logDataApi({ rsvnNo: bookingItem.rsvnNo, progress: "6", ref: "N" });

          setModalErrorMessage("체크아웃에 실패 하였습니다.");
          if (checkOutResponse.code === "3011") {
            Sentry.captureMessage(
              `checkOut_Amount_Error : 퇴실일자를 확인하시기 바랍니다. 체크아웃은 당일만 가능합니다. (예약번호:${bookingItem.rsvnNo})`
            );
            throw new Error("퇴실일자를 확인하시기 바랍니다. 체크아웃은 당일만 가능합니다.");
          } else {
            Sentry.captureMessage(
              `checkOut_response_Error : ${checkOutResponse.code}: ${checkOutResponse.message} (예약번호:${bookingItem.rsvnNo})`
            );
            throw new Error(`${checkOutResponse.code}, ${checkOutResponse.message}`);
          }
        }

        GoogleAnalytics.customEvent({
          category: "api_response",
          action: "api_response_ok_checkout_request",
        });
        logDataApi({ rsvnNo: bookingItem.rsvnNo, progress: "6", ref: "Y" });
        navigation("/checkout/payment/success");
      }
    } catch (error: any) {
      logDataApi({ rsvnNo: bookingItem.rsvnNo, progress: "6", ref: "N" });
      setModalErrorMessage(error.response?.data?.message);
      setModalErrorSubMessage(error.message);
      openModalError();
    } finally {
      setIsLoading(false);
    }
  };

  const frontPage = () => {
    navigation("/frontInfo");
  };

  useEffect(() => {
    //if (!_.isEmpty(urlQueryStringParams)) dispatch(bookingListAction.setBookingQueryStringParams(urlQueryStringParams));
    listBooking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (bookingItem.rsvnNo && bookingItem.rsvnStatusCode === "RR") {
      GoogleAnalytics.customEvent({
        category: "page_view",
        action: "page_view_booking_info",
        label: `${bookingItem.rsvnNo}_${bookingItem.rsvnStatusCode}_${bookingItem.guestName}`,
      });
    } else if (bookingItem.rsvnNo && bookingItem.rsvnStatusCode !== "RR") {
      GoogleAnalytics.customEvent({
        category: "page_view",
        action: "page_view_stay_info",
        label: `${bookingItem.rsvnNo}_${bookingItem.rsvnStatusCode}_${roomInfo.inhsGestName || bookingItem.guestName}`,
      });
    }
  }, [bookingItem.guestName, bookingItem.rsvnNo, bookingItem.rsvnStatusCode, roomInfo.inhsGestName]);

  return (
    <div {...props}>
      {!_.isEmpty(bookingItem) && bookingItem.rsvnStatusCode === "CI" ? ( // 체크인, 투숙 조회 api 완료되면 원복
        <MainLayout
          customStyle={{ position: "relative", margin: 0 }}
          ContentBody={<CheckedInBooking bookingItem={{ ...bookingItem, ...roomInfo }} checkOut={checkOut} />}
        />
      ) : (
        <MainLayout
          ContentBody={
            <InitialBooking
              bookingItem={bookingItem}
              setModalErrorMessage={setModalErrorMessage}
              setModalErrorSubMessage={setModalErrorSubMessage}
              openModalError={openModalError}
            />
          }
        />
      )}
      <ModalError isOpen={isOpenModalError} message={modalErrorMessage} subMessage={modalErrorSubMessage} onClose={closeModalError} />
      <Spinner isLoading={isLoading} />
    </div>
  );
};

export default InitialBookingContainer;
